import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  IconButton,
  Skeleton
} from "@mui/material";
import {
  SportsSoccer,
  SportsBasketball,
  SportsCricket,
  SportsHockey,
  ArrowForward,
  CheckCircleOutline,
  ArrowBack,
  ArrowForward as ArrowForwardIcon
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import {
  categoriesForHomeRow1,
  categoriesForHomeRow2
} from "../utils/homeCategoryRows";
import { publicUploadUrl } from "../utils/mediaUrls";
import { scrollToFooterContact } from "../utils/scrollToFooter";

const apiUrl = process.env.REACT_APP_API_URL || "/api";

const APPLE_BG = "#f5f5f7";
const APPLE_BLUE = "#0071e3";

const CATEGORIES = [
  {
    id: 1,
    name: "Football",
    slug: "football",
    icon: <SportsSoccer sx={{ fontSize: 40 }} />,
    description: "Custom football kits and jerseys",
    color: "#000000",
    image: "/assets/home/category-football.jpg"
  },
  {
    id: 2,
    name: "Basketball",
    slug: "basketball",
    icon: <SportsBasketball sx={{ fontSize: 40 }} />,
    description: "Professional basketball uniforms",
    color: "#000000",
    image: "/assets/home/category-basketball.jpg"
  },
  {
    id: 3,
    name: "Cricket",
    slug: "cricket",
    icon: <SportsCricket sx={{ fontSize: 40 }} />,
    description: "Cricket team kits and jerseys",
    color: "#000000",
    image: "/assets/home/category-cricket.jpg"
  },
  {
    id: 4,
    name: "Hockey",
    slug: "hockey",
    icon: <SportsHockey sx={{ fontSize: 40 }} />,
    description: "Hockey team uniforms",
    color: "#000000",
    image: "/assets/home/category-hockey.jpg"
  }
];

const KIT_TILE_META = {
  football: {
    tagline: "Design your kit. Built for the pitch.",
    gradient: "linear-gradient(180deg, #f5f5f7 0%, #e8e8ed 55%, #f5f5f7 100%)",
    image: "/assets/home/category-football.jpg"
  },
  basketball: {
    tagline: "Court-ready uniforms. Your colours.",
    gradient: "linear-gradient(180deg, #e8f4fc 0%, #d4ebf9 50%, #f5f5f7 100%)",
    image: "/assets/home/category-basketball.jpg"
  },
  cricket: {
    tagline: "From club whites to bold tour kits.",
    gradient: "linear-gradient(180deg, #f0f7f0 0%, #e2efe2 50%, #f5f5f7 100%)",
    image: "/assets/home/category-cricket.jpg"
  },
  hockey: {
    tagline: "Performance teamwear for every match.",
    gradient: "linear-gradient(180deg, #f5f5f7 0%, #ebebeb 50%, #f5f5f7 100%)",
    image: "/assets/home/category-hockey.jpg"
  }
};

const applePillPrimary = {
  borderRadius: "980px",
  px: 2.5,
  py: 1.25,
  fontSize: "13px",
  lineHeight: 1.3,
  fontWeight: 400,
  textTransform: "none",
  bgcolor: APPLE_BLUE,
  color: "#fff",
  boxShadow: "none",
  width: "auto",
  minHeight: "unset",
  whiteSpace: "nowrap",
  flexShrink: 0,
  "& .MuiButton-endIcon": {
    marginLeft: "6px",
    "& > *:nth-of-type(1)": { fontSize: "13px" }
  },
  "&:hover": { bgcolor: "#0077ed", boxShadow: "none" }
};

const applePillOutline = {
  borderRadius: "980px",
  px: 2.5,
  py: 1.25,
  fontSize: "13px",
  lineHeight: 1.3,
  fontWeight: 400,
  textTransform: "none",
  width: "auto",
  minHeight: "unset",
  whiteSpace: "nowrap",
  flexShrink: 0,
  color: APPLE_BLUE,
  borderColor: APPLE_BLUE,
  borderWidth: 1,
  bgcolor: "transparent",
  "&:hover": {
    bgcolor: "rgba(0,113,227,0.06)",
    borderColor: APPLE_BLUE,
    borderWidth: 1
  }
};

function resolveCategoryImage(category) {
  if (category.image) {
    if (category.image.startsWith("http") || category.image.startsWith("/")) {
      return category.image;
    }
    return publicUploadUrl(category.image);
  }
  if (category.image_filename) {
    return publicUploadUrl(category.image_filename);
  }
  return KIT_TILE_META[category.slug]?.image || "/assets/home/category-football.jpg";
}

function resolveCategoryTagline(category) {
  return (
    category.description ||
    KIT_TILE_META[category.slug]?.tagline ||
    "Bespoke teamwear designed for your squad."
  );
}

function resolveCategoryGradient(category) {
  return (
    KIT_TILE_META[category.slug]?.gradient ||
    "linear-gradient(180deg, #f5f5f7 0%, #e8e8ed 100%)"
  );
}

const CLIENT_SLIDES = [
  {
    id: 1,
    heading: "Nuneaton Town FC",
    description: "Nuneaton Town Football Club #BackingTheBoro",
    image: "/assets/home/slide-1.jpg"
  },
  {
    id: 2,
    heading: "Birmingham Blaze FC",
    description: "Elite performance kits tailored for Birmingham Blaze.",
    image: "/assets/home/slide-2.jpg"
  },
  {
    id: 3,
    heading: "WSCFC",
    description: "Wolverhampton Sporting Community – bespoke kits built to win.",
    image:
      "https://images.unsplash.com/photo-1518091043644-c1d4457512c6?auto=format&fit=crop&w=1200&q=80"
  },
  {
    id: 4,
    heading: "FC Clacton",
    description: "Game-ready designs for the home of The Seasiders.",
    image: "/assets/home/slide-4.jpg"
  }
];

const WHY_US_POINTS = [
  "4 week turnaround on playing kit and leisurewear",
  "Men's and Women's fits available",
  "Any design is possible on playing kit",
  "Minimum orders of 10 per product",
  "Full 'on-court' and 'off-court' product ranges",
  "Products tested on Elite Athletes"
];

function AppleKitTile({ category }) {
  const imageUrl = resolveCategoryImage(category);
  const tagline = resolveCategoryTagline(category);
  const gradient = resolveCategoryGradient(category);

  return (
    <Box
      component="article"
      sx={{
        position: "relative",
        borderRadius: { xs: 1.5, md: 2 },
        overflow: "hidden",
        background: gradient,
        width: "100%",
        minHeight: { xs: 520, sm: 580, md: 660 },
        height: "100%",
        display: "grid",
        gridTemplateRows: "auto 1fr auto",
        alignItems: "stretch",
        textAlign: "center",
        transition: "transform 0.35s ease",
        "&:hover": {
          transform: { md: "scale(1.008)" }
        }
      }}
    >
      {/* Top: title + details */}
      <Stack
        spacing={{ xs: 0.75, md: 1 }}
        alignItems="center"
        sx={{
          width: "100%",
          px: { xs: 2, md: 3 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 1, md: 1.5 }
        }}
      >
        <Typography
          component="h3"
          sx={{
            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.75rem" },
            fontWeight: 600,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            color: "#1d1d1f"
          }}
        >
          {category.name}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: "1rem", sm: "1.0625rem", md: "1.1875rem" },
            fontWeight: 400,
            color: "#6e6e73",
            maxWidth: 340,
            lineHeight: 1.45,
            mx: "auto"
          }}
        >
          {tagline}
        </Typography>
      </Stack>

      {/* Middle: kit image — fills available space */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minHeight: { xs: 260, sm: 300, md: 380 },
          px: { xs: 0.5, md: 1 },
          py: { xs: 0.5, md: 1 },
          overflow: "hidden"
        }}
      >
        <Box
          component="img"
          src={imageUrl}
          alt={category.name}
          sx={{
            display: "block",
            maxWidth: "98%",
            maxHeight: { xs: 300, sm: 360, md: 460 },
            width: "auto",
            height: "auto",
            objectFit: "contain",
            objectPosition: "center",
            userSelect: "none",
            pointerEvents: "none"
          }}
        />
      </Box>

      {/* Bottom: buttons */}
      <Stack
        direction="row"
        flexWrap="wrap"
        spacing={1}
        justifyContent="center"
        alignItems="center"
        useFlexGap
        sx={{
          width: "100%",
          mx: "auto",
          px: { xs: 2, md: 3 },
          pb: { xs: 3, md: 4 },
          pt: { xs: 1, md: 1.5 }
        }}
      >
        <Button
          component={Link}
          to={`/${category.slug}`}
          variant="contained"
          disableElevation
          sx={applePillPrimary}
        >
          Design now
        </Button>
        <Button
          variant="outlined"
          sx={applePillOutline}
          onClick={scrollToFooterContact}
        >
          Contact us
        </Button>
      </Stack>
    </Box>
  );
}

function KitTileSkeleton() {
  return (
    <Skeleton
      variant="rounded"
      sx={{
        borderRadius: { xs: 1.5, md: 2 },
        width: "100%",
        minHeight: { xs: 520, sm: 580, md: 660 },
        bgcolor: "#e8e8ed"
      }}
    />
  );
}

function Home() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeWordIndex, setActiveWordIndex] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const categoriesRef = useRef(null);

  const rotatingWords = useMemo(
    () => ["Innovative", "Bespoke", "Quality", "Reliable"],
    []
  );

  useEffect(() => {
    async function fetchCategories() {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/categories`);
        setCategories(response.data);
      } catch (error) {
        setCategories(CATEGORIES);
      } finally {
        setLoading(false);
      }
    }

    fetchCategories();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveWordIndex((prev) => (prev + 1) % rotatingWords.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [rotatingWords.length]);

  useEffect(() => {
    const slideTimer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % CLIENT_SLIDES.length);
    }, 5000);

    return () => clearInterval(slideTimer);
  }, []);

  const handlePrevSlide = () => {
    setActiveSlide((prev) => (prev === 0 ? CLIENT_SLIDES.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    setActiveSlide((prev) => (prev + 1) % CLIENT_SLIDES.length);
  };

  const currentSlide = CLIENT_SLIDES[activeSlide];

  const handleScrollToCategories = () => {
    if (categoriesRef.current) {
      categoriesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const homeGridCategories = useMemo(() => {
    const row1 = categoriesForHomeRow1(categories, loading, CATEGORIES);
    const row2 = categoriesForHomeRow2(categories, loading);
    return [...row1, ...row2];
  }, [categories, loading]);

  const renderHeroSection = () => (
    <Box
      component="section"
      sx={{
        position: "relative",
        width: "100%",
        minHeight: { xs: "auto", md: "100vh" },
        display: "flex",
        flexDirection: { xs: "column", md: "block" },
        overflow: "hidden",
        bgcolor: { xs: "#1d1d1f", md: "#0a0a0a" }
      }}
    >
      {/* Background image — full bleed on desktop, top band on mobile */}
      <Box
        sx={{
          position: { xs: "relative", md: "absolute" },
          inset: { md: 0 },
          width: "100%",
          height: { xs: "min(52vh, 440px)", md: "100%" },
          minHeight: { xs: 300, md: "100%" },
          flexShrink: 0,
          zIndex: 0
        }}
      >
        <Box
          component="img"
          src="/assets/home/JS68201971.avif"
          alt=""
          aria-hidden
          sx={{
            width: "100%",
            height: "100%",
            display: "block",
            objectFit: "cover",
            objectPosition: { xs: "center 30%", md: "center" }
          }}
        />
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: {
              xs: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 70%, #1d1d1f 100%)",
              md: "linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.45))"
            },
            zIndex: 1
          }}
        />
      </Box>

      <Box
        sx={{
          position: { xs: "relative", md: "absolute" },
          inset: { md: 0 },
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          py: { xs: 5, sm: 6, md: 12 },
          px: { xs: 2.5, md: 0 },
          mt: { xs: -6, md: 0 }
        }}
      >
        <Container maxWidth="md">
          <Stack spacing={{ xs: 3, md: 4 }} alignItems="center">
            <Typography
              component="h1"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "2.25rem", sm: "2.75rem", md: "4rem" },
                lineHeight: 1.08,
                letterSpacing: "-0.02em",
                color: "#fff",
                textShadow: "0 2px 24px rgba(0,0,0,0.35)"
              }}
            >
              <Box
                component="span"
                sx={{ color: "secondary.main", display: "block", mb: 0.5 }}
              >
                {rotatingWords[activeWordIndex]}
              </Box>
              <Box component="span" sx={{ color: "#fff", display: "block" }}>
                Teamwear Solutions
              </Box>
            </Typography>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              justifyContent="center"
              alignItems="center"
            >
              <Button
                variant="contained"
                disableElevation
                endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
                onClick={handleScrollToCategories}
                sx={{
                  ...applePillPrimary,
                  bgcolor: APPLE_BLUE,
                  whiteSpace: "nowrap",
                  flexShrink: 0
                }}
              >
                Start designing
              </Button>
              <Button
                variant="outlined"
                onClick={scrollToFooterContact}
                sx={{
                  ...applePillOutline,
                  borderColor: "rgba(255,255,255,0.85)",
                  color: "#fff",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255,255,255,0.12)"
                  }
                }}
              >
                Talk to our team
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );

  const renderKitGrid = (categoryList, { anchorCategories = false } = {}) => {
    if (!loading && categoryList.length === 0) return null;

    return (
      <Box
        ref={anchorCategories ? categoriesRef : undefined}
        sx={{
          bgcolor: APPLE_BG,
          py: { xs: 2, md: 2.5 },
          width: "100%",
          boxSizing: "border-box"
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
            gridAutoRows: "1fr",
            alignItems: "stretch",
            gap: { xs: "12px", md: "16px" },
            width: "100%",
            boxSizing: "border-box",
            px: { xs: "12px", md: "16px" }
          }}
        >
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <KitTileSkeleton key={`sk-${index}`} />
              ))
            : categoryList.map((category) => (
                <AppleKitTile key={category.id} category={category} />
              ))}
        </Box>
      </Box>
    );
  };

  const renderClientHighlights = () => (
    <Box component="section" sx={{ width: "100%", bgcolor: "#fff" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          width: "100%",
          minHeight: { md: "min(85vh, 780px)" },
          alignItems: "stretch"
        }}
      >
        <Box
          sx={{
            position: "relative",
            minHeight: { xs: 400, md: "100%" },
            width: "100%",
            color: "white",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            p: { xs: 3, md: 4 },
            order: { xs: 1, md: 1 },
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(rgba(0,0,0,0.35), rgba(0,0,0,0.45)), url(${currentSlide.image})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              zIndex: 0
            }
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              mb: 1,
              position: "relative",
              zIndex: 1,
              letterSpacing: "-0.02em",
              fontSize: { xs: "1.75rem", md: "2.25rem" }
            }}
          >
            {currentSlide.heading}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              opacity: 0.92,
              position: "relative",
              zIndex: 1,
              fontSize: { xs: "1.0625rem", md: "1.1875rem" },
              maxWidth: 480
            }}
          >
            {currentSlide.description}
          </Typography>
          <Box
            sx={{
              position: "absolute",
              top: { xs: 12, md: 20 },
              right: { xs: 12, md: 20 },
              display: "flex",
              gap: 1,
              zIndex: 2
            }}
          >
            <IconButton
              onClick={handlePrevSlide}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.45)",
                color: "white",
                backdropFilter: "blur(8px)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.65)" }
              }}
              aria-label="Previous slide"
            >
              <ArrowBack fontSize="small" />
            </IconButton>
            <IconButton
              onClick={handleNextSlide}
              size="small"
              sx={{
                bgcolor: "rgba(0,0,0,0.45)",
                color: "white",
                backdropFilter: "blur(8px)",
                "&:hover": { bgcolor: "rgba(0,0,0,0.65)" }
              }}
              aria-label="Next slide"
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 3, sm: 5, md: 6, lg: 12 },
            py: { xs: 7, md: 8 },
            bgcolor: "#fff",
            order: { xs: 2, md: 2 }
          }}
        >
          <Stack spacing={3} sx={{ maxWidth: 520, width: "100%" }}>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "#1d1d1f",
                fontSize: { xs: "2rem", md: "2.75rem" },
                lineHeight: 1.08
              }}
            >
              Enquiry / design
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6e6e73",
                lineHeight: 1.5,
                fontSize: { xs: "1.125rem", md: "1.25rem" }
              }}
            >
              From playing kit to leisurewear, our in-house team and kit builder help you
              get the right look — with the turnaround times and product ranges you need for
              your squad.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6e6e73",
                lineHeight: 1.5,
                fontSize: { xs: "1.125rem", md: "1.25rem" }
              }}
            >
              <Box component="span" sx={{ color: "#1d1d1f", fontWeight: 600 }}>
                Minimum orders:
              </Box>{" "}
              10 per product. Men&apos;s and Women&apos;s fits available. Start in the
              designer or contact us for a bespoke brief.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} flexWrap="wrap">
              <Button
                variant="contained"
                disableElevation
                endIcon={<ArrowForward sx={{ fontSize: 13 }} />}
                sx={applePillPrimary}
                onClick={scrollToFooterContact}
              >
                Request a quote
              </Button>
              <Button
                component={Link}
                to="/football"
                variant="outlined"
                sx={applePillOutline}
              >
                Launch kit designer
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );

  const renderWhyUsSection = () => (
    <Box component="section" sx={{ width: "100%", bgcolor: APPLE_BG }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          width: "100%",
          minHeight: { md: "min(85vh, 820px)" },
          alignItems: "stretch"
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 3, sm: 5, md: 6, lg: 12 },
            py: { xs: 7, md: 8 },
            order: { xs: 2, md: 1 }
          }}
        >
          <Box sx={{ maxWidth: 560, width: "100%" }}>
            <Typography
              variant="overline"
              sx={{
                color: APPLE_BLUE,
                fontWeight: 600,
                letterSpacing: "0.08em",
                fontSize: "0.875rem"
              }}
            >
              Why Zaror
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 600,
                letterSpacing: "-0.02em",
                color: "#1d1d1f",
                mb: 3,
                mt: 1,
                fontSize: { xs: "2rem", md: "2.75rem" },
                lineHeight: 1.08
              }}
            >
              Clubs, tours, schools & universities
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6e6e73",
                mb: 2,
                lineHeight: 1.5,
                fontSize: { xs: "1.125rem", md: "1.25rem" }
              }}
            >
              At Zaror we offer more than just kit for our partner teams. We pride ourselves
              on building long-term relationships with clubs across the UK.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#6e6e73",
                mb: 3,
                lineHeight: 1.5,
                fontSize: { xs: "1.125rem", md: "1.25rem" }
              }}
            >
              We have an in-house design team that can work with you to create the perfect
              kit for your club, tour, school or university.
            </Typography>
            <Stack spacing={1.75}>
              {WHY_US_POINTS.map((point, index) => (
                <Stack direction="row" spacing={1.5} alignItems="flex-start" key={index}>
                  <CheckCircleOutline
                    sx={{ color: APPLE_BLUE, fontSize: 22, mt: 0.25, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#1d1d1f",
                      fontSize: { xs: "1.0625rem", md: "1.125rem" },
                      lineHeight: 1.45
                    }}
                  >
                    {point}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            minHeight: { xs: 400, md: "100%" },
            width: "100%",
            overflow: "hidden",
            order: { xs: 1, md: 2 }
          }}
        >
          <Box
            component="img"
            src="/assets/home/why-us.jpg"
            alt="Zaror Sports teamwear"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block"
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ bgcolor: APPLE_BG, width: "100%", overflowX: "hidden" }}>
      {renderHeroSection()}
      {renderKitGrid(homeGridCategories, { anchorCategories: true })}
      {renderClientHighlights()}
      {renderWhyUsSection()}
    </Box>
  );
}

export default Home;
